import React, { useState, useEffect, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { CheckoutSection } from 'checkout/components/styles/common';
import OrderType from 'checkout/components/sections/order-type';
import Medical from 'checkout/components/sections/medical';
import { PaymentSelection } from 'src/checkout/components/sections/payment/selection';
import Additional from 'checkout/components/sections/additional';
import Customer from 'checkout/components/sections/user/customer';
import Time from 'checkout/components/sections/time';
import GuestCustomerInfo from 'checkout/components/sections/user/guest';
import InlineLogin from 'checkout/components/sections/user/inline-login';
import InlineSignup from 'checkout/components/sections/user/inline-signup';
import { ExternalUser } from 'checkout/components/sections/user/external';
import Rewards from 'checkout/components/sections/rewards';
import RewardsConnect from 'checkout/components/sections/rewards/connect';
import ScheduledOrders from 'checkout/components/sections/scheduled-orders';
import Summary from 'checkout/components/sections/summary';
import { Subscription } from 'checkout/components/sections/subscription';
import useStores from 'shared/hooks/use-stores';
import { areSpecialHoursToday, collectScheduledData, getEnabledV2OrderTypes } from 'shared/core/helpers/dispensaries';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { RewardsConnectionStatuses, RewardsBrands } from 'shared/constants';
import { DutchiePaySection } from 'src/payments/components';
import { useDutchiePay } from 'src/payments/hooks';
import { isDutchiePlusKioskOrder } from 'shared/helpers/checkout';
import useMediaQuery from 'shared/hooks/use-media-query';
import { mediaQueries } from 'shared/styles';
import { MMURAllotment } from 'checkout/components/sections/mmur-allotment';
import { useAllotmentCheck } from 'checkout/hooks/use-allotment-check';
import { InlineSocialLogin } from './user/inline-social-login';
import { RoundupForCharity } from './roundup/roundup-for-charity';
import { useIsRoundupForCharityEnabled } from './roundup/use-roundup-for-charity';

const { failed, ready, successful } = RewardsConnectionStatuses;

function CheckoutOptions() {
  const { Cart, User, UI } = useStores();
  const isLargePhoneOrSmaller = useMediaQuery(mediaQueries.largePhone);
  const { order, isDelivery, updateInlineAuth } = Cart;
  const { dispensary, paymentMethod, isAfterHoursOrder } = order;
  const [rewardAuthStatus, setRewardAuthStatus] = useState(Cart.rewardAuth?.pinConfirmed ? successful : ready);
  const isDutchiePlusKiosk = isDutchiePlusKioskOrder(Cart.orderType, UI.isPlus);
  const showMedicalSection = (UI.isPlus && order.medicalOrder) || (!UI.isPlus && dispensary.medicalDispensary);
  const [inputFieldError, setInputFieldError] = useState([]);
  const { allowDutchiePayEnrollment } = useDutchiePay();
  const flags = useFlags();
  const isRoundupForCharityEnabled = useIsRoundupForCharityEnabled();
  const { allotmentInfo, loading: allotmentLoading, shouldShowAllotment, hasAllotmentError } = useAllotmentCheck();

  const enabledV2OrderTypes = getEnabledV2OrderTypes(dispensary);
  const disableGuestCheckout = dispensary?.storeSettings?.disableGuestCheckout;
  const isGuest = !User.exists && !User.hasExternalUser;
  const { isLoggedIn } = User ?? {};

  // Note: If a Plus dispensary enables `disableGuestCheckout` and also includes `hideLogin=true` in the URL,
  // users will be unable to complete checkout. These settings conflict, and we've decided that `hideLogin` takes precedence.
  const showInlineAuth = isGuest && disableGuestCheckout && !UI.hideLogin;
  const showGuest = isGuest && !disableGuestCheckout;
  const showCustomer = User.exists && !User.hasExternalUser;
  const showExternalUser = User.hasExternalUser;
  const showAdditional =
    (isDelivery && _.get(dispensary, `requiresDriversLicense`, false)) ||
    (!isDelivery && _.get(dispensary, `requiresDriversLicenseForPickup`, false)) ||
    paymentMethod === 'linx';

  const alpineRewardsEnabled = dispensary?.consumerDispensaryIntegrations?.alpineiq?.enabled;
  const springbigRewardsEnabled = dispensary?.consumerDispensaryIntegrations?.springbig?.enabled;
  const isDutchiePOSLoyaltyEnabled = dispensary?.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled;
  const hasAlpineRewardsWallet = Cart.hasRewardsWallet && _.toLower(Cart.rewardBrandName) === RewardsBrands.alpineiq;
  const phoneNumber = useMemo(() => {
    if (Cart.order.isGuestOrder) {
      return Cart.order?.guestCustomer?.phone;
    }
    return Cart.order?.inlineAuth?.phone || User.phone;
  }, [Cart.order.isGuestOrder, Cart.order?.guestCustomer?.phone, Cart.order?.inlineAuth?.phone, User.phone]);
  const isValidPhoneNumber = isMobilePhone(phoneNumber ?? '', `en-US`);
  const showAlpineRewards = hasAlpineRewardsWallet && Cart.rewardAuth?.pinConfirmed;
  const showRegularRewards = Cart.hasRewardsWallet && !hasAlpineRewardsWallet;
  const showAlpineSignup = !Cart.hasRewardsWallet && isValidPhoneNumber && alpineRewardsEnabled;
  const isShowSpringbigLoyaltyFlagEnabled = flags['growth.ecomm.springbig-member-signup.rollout'] ?? false;
  const showSpringbigSignup =
    !Cart.hasRewardsWallet &&
    isValidPhoneNumber &&
    springbigRewardsEnabled &&
    isShowSpringbigLoyaltyFlagEnabled &&
    isLoggedIn;
  const showRewards =
    showAlpineRewards ||
    showRegularRewards ||
    springbigRewardsEnabled ||
    showAlpineSignup ||
    showSpringbigSignup ||
    isDutchiePOSLoyaltyEnabled;

  const inlineAuth = Cart.inlineAuthType;

  const orderType = Cart.orderType === 'pickup' ? `inStorePickup` : Cart.orderType;

  const showScheduledOrders = dispensary?.orderTypesConfigV2[orderType]?.enableScheduledOrdering;

  const updateCartSchedulingInfo = useCallback(
    (schedulingOption = `asap`, reservationDropdownOptions = []) => {
      // Collect info on order type availability and reservation options when special hours are in effect
      const isSpecialHours = areSpecialHoursToday(dispensary);
      if (isSpecialHours) {
        Cart.setSchedulingData(
          collectScheduledData(dispensary, orderType, schedulingOption, reservationDropdownOptions)
        );
      }
    },
    [Cart, dispensary, orderType]
  );

  useEffect(() => {
    updateCartSchedulingInfo();
  }, [Cart.orderType, updateCartSchedulingInfo]);

  useEffect(() => {
    if (Cart.rewardAuth?.pinConfirmed) {
      setRewardAuthStatus(successful);
    }

    if (Cart.rewardAuth?.incorrectPinProvided) {
      setRewardAuthStatus(failed);
    }

    if (_.isEmpty(Cart.rewardAuth)) {
      setRewardAuthStatus(ready);
    }
  }, [Cart.rewardAuth]);

  const removeInputFieldError = (field) => {
    const remainderErrors = _.reject(inputFieldError, [`field`, field]);
    setInputFieldError([...remainderErrors]);
  };
  const handleInputChange = ({ target }) => {
    updateInlineAuth(target.name, target.value);
    removeInputFieldError(target.name);
  };

  const enableOmniauthLogin = dispensary?.storeSettings?.enableOmniauthLoginCheckout ?? false;
  const showInlineSocialLogin = showGuest && !enableOmniauthLogin && !UI.hideLogin;
  const showDutchiePayExpress = isGuest && allowDutchiePayEnrollment && enableOmniauthLogin;

  return (
    <Container>
      {showDutchiePayExpress && <DutchiePaySection />}
      {showInlineSocialLogin && <InlineSocialLogin />}
      <CheckoutSection noBorder>
        {showInlineAuth && inlineAuth === 'signup' && (
          <InlineSignup
            onShowLogin={() => Cart.setInlineAuthType(`login`)}
            handleInputChange={handleInputChange}
            inputFieldError={inputFieldError}
            setInputFieldError={setInputFieldError}
          />
        )}
        {showInlineAuth && inlineAuth === 'login' && (
          <InlineLogin
            onShowSignup={() => Cart.setInlineAuthType(`signup`)}
            handleInputChange={handleInputChange}
            inputFieldError={inputFieldError}
            setInputFieldError={setInputFieldError}
          />
        )}
        {showCustomer && <Customer />}
        {showGuest && <GuestCustomerInfo />}
        {showExternalUser && <ExternalUser />}
        {dispensary.storeSettings?.subscriptions?.enabled && !isDutchiePlusKiosk && <Subscription />}
        {alpineRewardsEnabled && hasAlpineRewardsWallet && (
          <RewardsConnect status={rewardAuthStatus} setRewardsStatus={setRewardAuthStatus} />
        )}
      </CheckoutSection>
      <CheckoutSection>
        {!isDutchiePlusKiosk && (
          <OrderType ref={Cart.orderTypeComponentRef} enabledV2OrderTypes={enabledV2OrderTypes} />
        )}
        {showAdditional && <Additional />}
        {isAfterHoursOrder && <Time />}

        {showScheduledOrders && (
          <ScheduledOrders ref={Cart.scheduledOrderingRef} updateCartSchedulingInfo={updateCartSchedulingInfo} />
        )}
        {/* Check Order Type and isolatedCheckout to hide medical card if dutchie plus and retail order */}
        {showMedicalSection && <Medical ref={Cart.medicalComponentRef} />}
      </CheckoutSection>
      {shouldShowAllotment && !hasAllotmentError && (
        <CheckoutSection>
          <MMURAllotment allotmentInfo={allotmentInfo} loading={allotmentLoading} />
        </CheckoutSection>
      )}
      {showRewards && (
        <CheckoutSection>
          <Rewards
            alpineRewardsEnabled={alpineRewardsEnabled}
            springbigRewardsEnabled={springbigRewardsEnabled}
            dutchiePosLoyaltyEnabled={isDutchiePOSLoyaltyEnabled}
          />
        </CheckoutSection>
      )}
      <Summary />
      {isRoundupForCharityEnabled && <RoundupForCharity />}
      {!isLargePhoneOrSmaller && (
        <CheckoutSection>
          <PaymentSelection />
        </CheckoutSection>
      )}
    </Container>
  );
}

export default observer(CheckoutOptions);

export const Container = styled.div`
  border-top: 1px solid var(--dcm-checkout-section-border-color);
`;
